/*
 * Carrusel de Colchones Fantasía: una fila que se desliza de izquierda a derecha.
 *
 *   <div data-fantasia-carrusel data-intervalo="3500">
 *     <ul class="fantasia-carrusel"> <li>…</li> … </ul>
 *     <button data-carrusel-anterior>  <button data-carrusel-siguiente>   (opcionales)
 *   </div>
 *
 * - Avanza solo, con movimiento suave, una tarjeta a la vez; al final vuelve al inicio.
 * - Se detiene mientras el cliente lo toca, hace clic o navega con el teclado.
 * - Solo se mueve si hay tarjetas que no caben (si caben todas, queda quieto).
 * - Sin movimiento automático si el sistema pide «reducir movimiento».
 */
(() => {
  const reducirMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FantasiaCarrusel {
    constructor(raiz) {
      this.raiz = raiz;
      this.pista = raiz.querySelector('.fantasia-carrusel');
      if (!this.pista) return;
      this.intervalo = Number(raiz.dataset.intervalo || 3500);
      this.auto = raiz.dataset.auto !== 'false'; // «Avanza solo» en el editor del tema
      this.pausado = false;
      this.reanudar = null;

      raiz.querySelector('[data-carrusel-anterior]')?.addEventListener('click', () => this.mover(-1, true));
      raiz.querySelector('[data-carrusel-siguiente]')?.addEventListener('click', () => this.mover(1, true));

      const pausar = () => this.pausar();
      const soltar = () => this.soltar();
      // Solo se detiene si el cliente interactúa (clic, arrastre, dedo, teclado); tener el mouse
      // encima no lo detiene (en computador el mouse casi siempre está sobre algo).
      this.pista.addEventListener('pointerdown', pausar);
      this.pista.addEventListener('pointerup', soltar);
      this.pista.addEventListener('touchstart', pausar, { passive: true });
      this.pista.addEventListener('touchend', soltar, { passive: true });
      this.pista.addEventListener('focusin', pausar);
      this.pista.addEventListener('focusout', soltar);
      this.pista.addEventListener('scroll', () => this.actualizarFlechas(), { passive: true });
      window.addEventListener('resize', () => this.actualizarFlechas());

      // Solo corre mientras se ve en pantalla (no gasta batería ni mueve cosas fuera de vista).
      this.visible = false;
      new IntersectionObserver((entradas) => {
        this.visible = entradas[0].isIntersecting;
      }, { threshold: 0.3 }).observe(this.pista);

      // Puntos indicadores (opcionales): uno por cada posición del carrusel; se pueden tocar.
      this.puntos = raiz.querySelector('[data-carrusel-puntos]');
      if (this.puntos) {
        this.pista.addEventListener('scroll', () => this.actualizarPuntos(), { passive: true });
        window.addEventListener('resize', () => this.construirPuntos());
        window.addEventListener('load', () => this.construirPuntos());
        this.construirPuntos();
      }

      this.actualizarFlechas();
      this.reloj = setInterval(() => this.tick(), this.intervalo);
    }

    desborda() {
      return this.pista.scrollWidth - this.pista.clientWidth > 4;
    }

    paso() {
      const primero = this.pista.children[0];
      if (!primero) return this.pista.clientWidth;
      const estilo = getComputedStyle(this.pista);
      const espacio = parseFloat(estilo.columnGap || estilo.gap || '0') || 0;
      return primero.getBoundingClientRect().width + espacio;
    }

    mover(direccion, manual = false) {
      if (!this.desborda()) return;
      const max = this.pista.scrollWidth - this.pista.clientWidth;
      const actual = this.pista.scrollLeft;
      let destino = actual + direccion * this.paso();
      if (direccion > 0 && actual >= max - 4) destino = 0; // al final, vuelve al inicio
      if (direccion < 0 && actual <= 4) destino = max;
      this.desplazar(destino);
      if (manual) this.pausar(6000);
    }

    /*
     * Movimiento propio, cuadro a cuadro: en iPhone (Safari y Chrome usan el mismo motor) el
     * desplazamiento suave del navegador choca con el encaje de tarjetas y el carrusel vuelve
     * a su lugar. Durante el movimiento se apagan el encaje y el desplazamiento suave del CSS, y
     * se encienden de nuevo solo cuando termina. Con «reducir movimiento» el cambio es directo.
     */
    desplazar(destino) {
      const pista = this.pista;
      const inicio = pista.scrollLeft;
      const distancia = destino - inicio;
      cancelAnimationFrame(this.animacion);

      if (Math.abs(distancia) < 1) return;

      const duracion = reducirMovimiento.matches ? 0 : Math.min(900, 380 + Math.abs(distancia) * 0.5);
      const suave = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const comienzo = performance.now();

      pista.style.scrollSnapType = 'none';
      pista.style.scrollBehavior = 'auto';

      const cuadro = (ahora) => {
        const t = duracion === 0 ? 1 : Math.min(1, (ahora - comienzo) / duracion);
        pista.scrollLeft = inicio + distancia * suave(t);

        if (t < 1) {
          this.animacion = requestAnimationFrame(cuadro);
        } else {
          this.animacion = requestAnimationFrame(() => {
            pista.style.scrollSnapType = '';
            pista.style.scrollBehavior = '';
          });
        }
      };
      this.animacion = requestAnimationFrame(cuadro);
    }

    tick() {
      // Con «reducir movimiento» sigue avanzando, pero sin deslizarse (cambio directo).
      if (!this.auto || this.pausado || !this.visible || document.hidden) return;
      this.mover(1);
    }

    pausar(ms) {
      this.pausado = true;
      clearTimeout(this.reanudar);
      if (ms) this.reanudar = setTimeout(() => (this.pausado = false), ms);
    }

    soltar() {
      clearTimeout(this.reanudar);
      this.reanudar = setTimeout(() => (this.pausado = false), 4000);
    }

    construirPuntos() {
      if (!this.puntos) return;
      const max = this.pista.scrollWidth - this.pista.clientWidth;
      const total = this.desborda() ? Math.round(max / this.paso()) + 1 : 0;
      if (this.puntos.children.length !== total) {
        this.puntos.replaceChildren();
        for (let i = 0; i < total; i++) {
          const punto = document.createElement('button');
          punto.type = 'button';
          punto.className = 'fantasia-carrusel__punto';
          punto.setAttribute('aria-label', `Ir al ${i + 1} de ${total}`);
          punto.addEventListener('click', () => {
            const tope = this.pista.scrollWidth - this.pista.clientWidth;
            this.desplazar(Math.min(i * this.paso(), tope));
            this.pausar(8000);
          });
          this.puntos.appendChild(punto);
        }
      }
      this.actualizarPuntos();
    }

    actualizarPuntos() {
      if (!this.puntos) return;
      const max = this.pista.scrollWidth - this.pista.clientWidth;
      const total = this.puntos.children.length;
      const activo = this.pista.scrollLeft >= max - 4 ? total - 1 : Math.round(this.pista.scrollLeft / this.paso());
      [...this.puntos.children].forEach((p, i) => p.classList.toggle('is-activo', i === activo));
    }

    actualizarFlechas() {
      this.raiz.classList.toggle('fantasia-carrusel--quieto', !this.desborda());
    }
  }

  /*
   * Cinta continua (franja de confianza): corre sola de derecha a izquierda y se puede arrastrar
   * con el dedo o el mouse hacia cualquier lado; al soltar sigue con algo de impulso y retoma sola.
   * Movimiento propio con transform (no la animación CSS): en iPhone y Android con «reducir
   * movimiento» o «quitar animaciones» la animación CSS se apagaba y la franja quedaba quieta y
   * sin poder deslizarse. Lleva dos copias iguales de la lista para dar la vuelta sin saltos.
   */
  class FantasiaCinta {
    constructor(raiz) {
      this.raiz = raiz;
      this.pista = raiz.querySelector('.fantasia-cinta__pista');
      if (!this.pista || this.pista.children.length < 2) return;
      this.segundos = Math.max(4, Number(raiz.dataset.segundos) || 36);
      this.x = 0;
      this.inercia = 0;
      this.arrastre = null;
      this.pausadoHasta = 0;
      this.enfocado = false;
      this.visible = true;
      this.ultimo = performance.now();

      raiz.classList.add('fantasia-cinta--js');
      raiz.querySelectorAll('img').forEach((img) => img.setAttribute('draggable', 'false'));

      this.medir();
      new ResizeObserver(() => this.medir()).observe(this.pista);
      new IntersectionObserver((e) => (this.visible = e[0].isIntersecting)).observe(raiz);

      raiz.addEventListener('pointerdown', (e) => this.tomar(e));
      raiz.addEventListener('pointermove', (e) => this.arrastrar(e));
      raiz.addEventListener('pointerup', (e) => this.soltar(e));
      raiz.addEventListener('pointercancel', (e) => this.soltar(e));
      // Tras arrastrar no se abre el enlace que quedó bajo el dedo.
      raiz.addEventListener('click', (e) => {
        if (this.recienArrastrada) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
      raiz.addEventListener('focusin', () => (this.enfocado = true));
      raiz.addEventListener('focusout', () => (this.enfocado = false));

      requestAnimationFrame((t) => this.cuadro(t));
    }

    // Ancho de una copia de la lista: al recorrerlo, la cinta vuelve a verse igual.
    medir() {
      const [a, b] = this.pista.children;
      this.vuelta = b.offsetLeft - a.offsetLeft;
      this.velocidad = this.vuelta / this.segundos;
    }

    tomar(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.arrastre = { id: e.pointerId, x0: e.clientX, desde: this.x, ultimoX: e.clientX, ultimoT: e.timeStamp, vel: 0, movio: false };
      this.inercia = 0;
    }

    arrastrar(e) {
      const a = this.arrastre;
      if (!a || e.pointerId !== a.id) return;
      const dx = e.clientX - a.x0;
      if (!a.movio) {
        if (Math.abs(dx) < 6) return;
        a.movio = true;
        this.raiz.classList.add('is-arrastrando');
        try { this.raiz.setPointerCapture(e.pointerId); } catch (err) { /* sin captura */ }
      }
      this.x = a.desde + dx;
      const dt = e.timeStamp - a.ultimoT;
      if (dt > 0) a.vel = 0.8 * ((e.clientX - a.ultimoX) / dt) * 1000 + 0.2 * a.vel;
      a.ultimoX = e.clientX;
      a.ultimoT = e.timeStamp;
    }

    soltar(e) {
      const a = this.arrastre;
      if (!a || e.pointerId !== a.id) return;
      this.arrastre = null;
      this.raiz.classList.remove('is-arrastrando');
      if (a.movio) {
        this.inercia = Math.max(-2500, Math.min(2500, a.vel));
        this.recienArrastrada = true;
        setTimeout(() => (this.recienArrastrada = false), 50);
      }
      this.pausadoHasta = performance.now() + 2500;
    }

    cuadro(t) {
      const dt = Math.min(0.05, (t - this.ultimo) / 1000);
      this.ultimo = t;
      if (!this.arrastre && this.vuelta > 0) {
        if (Math.abs(this.inercia) > 8) {
          this.x += this.inercia * dt;
          this.inercia *= Math.pow(0.05, dt); // se frena en ~1 s
        } else if (t > this.pausadoHasta && !this.enfocado && this.visible && !document.hidden) {
          this.x -= this.velocidad * dt;
        }
      }
      if (this.vuelta > 0) {
        while (this.x <= -this.vuelta) this.x += this.vuelta;
        while (this.x > 0) this.x -= this.vuelta;
      }
      if (this.visible) this.pista.style.transform = `translate3d(${this.x.toFixed(2)}px, 0, 0)`;
      requestAnimationFrame((s) => this.cuadro(s));
    }
  }

  const iniciar = (contexto = document) => {
    contexto.querySelectorAll('[data-fantasia-carrusel]').forEach((raiz) => {
      if (raiz.dataset.carruselListo) return;
      raiz.dataset.carruselListo = '1';
      new FantasiaCarrusel(raiz);
    });
    contexto.querySelectorAll('[data-fantasia-cinta]').forEach((raiz) => {
      if (raiz.dataset.cintaLista) return;
      raiz.dataset.cintaLista = '1';
      new FantasiaCinta(raiz);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => iniciar());
  } else {
    iniciar();
  }
  // Editor del tema: al agregar o cambiar una sección.
  document.addEventListener('shopify:section:load', (e) => iniciar(e.target));

  /*
   * Carruseles de productos del tema (Novedades, Los más vendidos): también avanzan solos.
   * Usan el Swiper del tema (swiper-component.initSwiper); misma regla de pausas.
   */
  const ajustes = window.fantasiaCarruseles || {};
  const INTERVALO_PRODUCTOS = Math.max(2, Number(ajustes.segundosProductos) || 4) * 1000;
  const productos = new Map();
  const vigilar = () => {
    document.querySelectorAll('[id*="featured_collection"] swiper-component').forEach((comp) => {
      if (productos.has(comp)) return;
      const estado = { pausado: false, visible: false, reanudar: null };
      productos.set(comp, estado);
      const pausar = () => { estado.pausado = true; clearTimeout(estado.reanudar); };
      const soltar = () => { clearTimeout(estado.reanudar); estado.reanudar = setTimeout(() => (estado.pausado = false), 4000); };
      comp.addEventListener('pointerdown', pausar);
      comp.addEventListener('pointerup', soltar);
      comp.addEventListener('touchstart', pausar, { passive: true });
      comp.addEventListener('touchend', soltar, { passive: true });
      new IntersectionObserver((e) => (estado.visible = e[0].isIntersecting), { threshold: 0.3 }).observe(comp);
    });
  };
  if (ajustes.productos !== false) setInterval(() => {
    vigilar();
    if (document.hidden) return;
    const directo = reducirMovimiento.matches;  // sin deslizarse, pero avanza igual
    productos.forEach((estado, comp) => {
      const swiper = comp.initSwiper;
      if (!swiper || estado.pausado || !estado.visible || swiper.destroyed) return;
      if (swiper.isEnd) swiper.slideTo(0, directo ? 0 : 900);
      else swiper.slideNext(directo ? 0 : 700);
    });
  }, INTERVALO_PRODUCTOS);
})();
